import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaEye, FaPlus } from 'react-icons/fa';
import { sendApiRequest } from '../../utils/api';

const MarkingRuleList = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await sendApiRequest('api/marking-rules', {}, 'GET');
      if (response) {
        setRules(response);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this marking rule?')) return;

    try {
      await sendApiRequest(`api/marking-rules/${id}`, {}, 'DELETE');
      setRules(rules.filter(rule => rule._id !== id));
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to delete rule');
    }
  };

  const viewRuleDetails = (rule) => {
    setSelectedRule(rule);
    setShowDetails(true);
  };

  const formatMistakes = (mistakes) => {
    if (!mistakes || mistakes.length === 0) return 'None';
    return mistakes.slice(0, 2).map(m => m.type).join(', ') +
           (mistakes.length > 2 ? ` +${mistakes.length - 2} more` : '');
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  if (error) return (
    <div className="text-red-500 text-center py-8 bg-red-50 rounded-lg">
      {error}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Marking Rules</h1>
          <p className="text-gray-600 mt-1">Manage typing rules for different exam categories</p>
        </div>
        <button
          onClick={() => navigate('/admin-panel/marking-rules/new')}
          className="flex items-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FaPlus className="mr-2" />
          Add New Rule
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-gray-500 mb-4">
            <FaPlus className="mx-auto h-12 w-12 mb-4 opacity-50" />
            <h3 className="text-lg font-medium">No marking rules found</h3>
            <p>Create your first typing rule to get started</p>
          </div>
          <button
            onClick={() => navigate('/admin/marking-rules/new')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Rule
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rule Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Exam Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Full Mistakes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Half Mistakes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Speed Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Passing Accuracy
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rules.map((rule) => (
                  <tr key={rule._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{rule.title}</div>
                      {rule.notes && (
                        <div className="text-sm text-gray-500 truncate max-w-xs" title={rule.notes}>
                          {rule.notes}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {rule.examCategory?.title || 'Not assigned'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatMistakes(rule.fullMistakes)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatMistakes(rule.halfMistakes)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {rule.speedUnit?.toUpperCase()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {rule.passingAccuracy}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rule.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {rule.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => viewRuleDetails(rule)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      {/* <button
                        onClick={() => navigate(`/admin/marking-rules/${rule._id}/edit`)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edit Rule"
                      >
                        <FaEdit />
                      </button> */}
                      <button
                        onClick={() => handleDelete(rule._id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Rule"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Rule Details Modal */}
      {showDetails && selectedRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">{selectedRule.title}</h3>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTrash className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Exam Category</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedRule.examCategory?.title || 'Not assigned'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Speed Unit</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedRule.speedUnit?.toUpperCase()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Passing Accuracy</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedRule.passingAccuracy}%</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedRule.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedRule.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {selectedRule.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedRule.notes}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-red-800 mb-3">Full Mistakes</h4>
                  {selectedRule.fullMistakes?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedRule.fullMistakes.map((mistake, index) => (
                        <div key={index} className="bg-white p-3 rounded border-l-4 border-red-500">
                          <div className="font-medium text-red-800">{mistake.type}</div>
                          <div className="text-sm text-gray-600">{mistake.description}</div>
                          <div className="text-sm font-medium">Penalty: {mistake.penalty}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-red-600 text-sm">No full mistakes defined</p>
                  )}
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-3">Half Mistakes</h4>
                  {selectedRule.halfMistakes?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedRule.halfMistakes.map((mistake, index) => (
                        <div key={index} className="bg-white p-3 rounded border-l-4 border-yellow-500">
                          <div className="font-medium text-yellow-800">{mistake.type}</div>
                          <div className="text-sm text-gray-600">{mistake.description}</div>
                          <div className="text-sm font-medium">Penalty: {mistake.penalty}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-yellow-600 text-sm">No half mistakes defined</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6 space-x-3">
              <button
                onClick={() => setShowDetails(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              {/* <button
                onClick={() => {
                  setShowDetails(false);
                  navigate(`/admin/marking-rules/${selectedRule._id}/edit`);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Edit Rule
              </button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkingRuleList;
